import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, BellOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import usePushNotifications from '@/hooks/usePushNotifications';

export default function PushNotificationPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { 
    isSupported, 
    isSubscribed, 
    permission, 
    subscribe, 
    isLoading 
  } = usePushNotifications();

  useEffect(() => {
    // Check if we should show the prompt
    const checkAndShowPrompt = () => {
      // Don't show if:
      // 1. Not supported
      // 2. Already subscribed
      // 3. Permission already denied (user blocked)
      // 4. User already dismissed this session
      // 5. User already saw prompt before (stored in localStorage)
      // 6. Cookie consent hasn't been handled yet (show cookie first)
      
      if (!isSupported) return;
      if (isSubscribed) return;
      if (permission === 'denied') return;
      if (dismissed) return;
      
      const hasSeenPrompt = localStorage.getItem('viraly_push_prompt_seen');
      if (hasSeenPrompt) return;
      
      // Only show if cookie consent has been given (registration flow complete)
      const cookieConsent = localStorage.getItem('viraly_cookie_consent');
      if (!cookieConsent) return;
      
      // Show prompt after a short delay (let user see the dashboard first)
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 2500);
      
      return () => clearTimeout(timer);
    };
    
    checkAndShowPrompt();
  }, [isSupported, isSubscribed, permission, dismissed]);

  const handleEnable = async () => {
    try {
      const success = await subscribe();
      if (success) {
        localStorage.setItem('viraly_push_prompt_seen', 'accepted');
        setShowPrompt(false);
      } else {
        // If subscription failed, still dismiss the prompt but don't mark as accepted
        // This way they can try again in settings
        localStorage.setItem('viraly_push_prompt_seen', 'failed');
        setShowPrompt(false);
      }
    } catch (err) {
      console.error('Notification subscription error:', err);
      localStorage.setItem('viraly_push_prompt_seen', 'failed');
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('viraly_push_prompt_seen', 'dismissed');
    setDismissed(true);
    setShowPrompt(false);
  };

  const handleRemindLater = () => {
    // Don't store in localStorage - will show again next session
    setDismissed(true);
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-viraly-card rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
        >
          {/* Header */}
          <div className="relative p-6 pb-0">
            <button
              onClick={handleRemindLater}
              className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-viraly-muted" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 pt-2 text-center">
            {/* Animated Bell Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", bounce: 0.5, delay: 0.1 }}
              className="w-20 h-20 rounded-2xl bg-viraly-gradient flex items-center justify-center mx-auto mb-6"
            >
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                <Bell className="w-10 h-10 text-white" />
              </motion.div>
            </motion.div>

            <h2 className="font-heading text-2xl font-bold mb-3">
              Stay in the Loop!
            </h2>
            
            <p className="text-viraly-muted mb-6">
              Get instant notifications when your AI content is ready, 
              posts are published, and more. Never miss an update!
            </p>

            {/* Benefits */}
            <div className="text-left bg-white/5 rounded-xl p-4 mb-6">
              <p className="text-sm font-medium mb-2">You'll be notified when:</p>
              <ul className="text-sm text-viraly-muted space-y-1.5">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-viraly-success" />
                  Your scheduled posts are published
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-viraly-orange" />
                  Your AI-generated content is ready
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" />
                  You're running low on credits
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                  Important updates & tips
                </li>
              </ul>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <Button
                onClick={handleEnable}
                disabled={isLoading}
                className="btn-primary w-full py-3"
                data-testid="enable-push-btn"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    Enabling...
                  </span>
                ) : (
                  <>
                    <Bell className="w-5 h-5 mr-2" />
                    Enable Notifications
                  </>
                )}
              </Button>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleRemindLater}
                  className="btn-secondary flex-1 py-2.5 text-sm"
                >
                  Remind Me Later
                </Button>
                <Button
                  onClick={handleDismiss}
                  className="flex-1 py-2.5 text-sm bg-transparent hover:bg-white/5 text-viraly-muted"
                >
                  <BellOff className="w-4 h-4 mr-1" />
                  No Thanks
                </Button>
              </div>
            </div>

            <p className="text-xs text-viraly-muted mt-4">
              You can change this anytime in Settings → Notifications
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
